import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useSoundPacks } from '../../context/SoundPacksContext';
import { DatabaseSoundPackSoundsModel, DatabaseSound } from '../../services/DatabaseSoundPackSounds';
import { formatDuration } from '../../helpers/formatDuration';
import { decodeThumbnail } from '../../helpers/decodeThumbnail';
import WaveformThumbnail from '../WaveformThumbnail';

// One entry in the cross-pack ranked-sounds reference set (see rankedEntries).
type RankedEntry = { uid: string; rank: number };

// Admin-only: reorder a pack's sound list. A dedicated route (rather than an
// expandable section in Edit Preview) so only one pack can ever be in
// reordering mode at a time. Order is nothing but array position in the
// sound_packs_sounds doc, so publishing is just an ordinary setPackSounds
// write of the locally-reordered array — no separate reorder API needed.
//
// Base and demo sounds are edited as two independent lists (mirroring the
// pack's base/ and demo/ folders) and always republished as two contiguous
// blocks, demo first, so this tool never interleaves the two once it's
// been used.
function ReorderSounds() {
    const { packId } = useParams<{ packId: string }>();
    const navigate = useNavigate();
    const { isAdmin, isLoading: userLoading } = useUser();
    const { soundPacks } = useSoundPacks();
    const pack = soundPacks.find(p => p.id === packId);

    const [baseSounds, setBaseSounds] = useState<DatabaseSound[] | null>(null);
    const [demoSounds, setDemoSounds] = useState<DatabaseSound[] | null>(null);
    const [savedOrder, setSavedOrder] = useState<string[]>([]);
    const [savedRanks, setSavedRanks] = useState<Record<string, number | null>>({});
    const [loading, setLoading] = useState(true);
    const [publishing, setPublishing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Every ranked sound currently in use, across every pack (not just this
    // one) — the reference set for three things: "Push to top" (one above the
    // max), the "→ #N of M" position hint next to each rank input, and the
    // read-only ranked-list panel, so an admin can see the whole cross-pack
    // "All Samples" order without leaving this page. null while the initial
    // fetch is pending. Kept in sync locally as ranks are edited below (see
    // setRank), so all three stay accurate through a whole editing session
    // without re-fetching.
    const [rankedEntries, setRankedEntries] = useState<RankedEntry[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        DatabaseSoundPackSoundsModel.getAllPackSounds()
            .then(allPackSounds => {
                if (cancelled) return;
                const entries = allPackSounds
                    .flatMap(p => p.sounds)
                    .flatMap((s): RankedEntry[] => s.rank !== null ? [{ uid: s.uid, rank: s.rank }] : []);
                setRankedEntries(entries);
            })
            .catch(err => console.error('Failed to load current ranks:', err));
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!packId) return;
        let cancelled = false;
        setLoading(true);
        DatabaseSoundPackSoundsModel.getPackSounds(packId)
            .then(result => {
                if (cancelled) return;
                setBaseSounds(result.sounds.filter(s => !s.isDemo));
                setDemoSounds(result.sounds.filter(s => s.isDemo));
                setSavedOrder(result.sounds.map(s => s.uid));
                setSavedRanks(Object.fromEntries(result.sounds.map(s => [s.uid, s.rank])));
            })
            .catch(err => {
                if (cancelled) return;
                console.error('Failed to load pack sounds:', err);
                setError('Failed to load sound list.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [packId]);

    // Compares uid order and rank — the only two things this tool can change.
    const isDirty = useMemo(() => {
        if (!baseSounds || !demoSounds) return false;
        const combined = [...demoSounds, ...baseSounds];
        if (combined.length !== savedOrder.length) return true;
        return combined.some((s, i) => s.uid !== savedOrder[i] || s.rank !== (savedRanks[s.uid] ?? null));
    }, [baseSounds, demoSounds, savedOrder, savedRanks]);

    const moveSound = (
        list: DatabaseSound[],
        setList: React.Dispatch<React.SetStateAction<DatabaseSound[] | null>>,
        index: number,
        direction: -1 | 1,
    ) => {
        const target = index + direction;
        if (target < 0 || target >= list.length) return;
        const next = [...list];
        [next[index], next[target]] = [next[target], next[index]];
        setList(next);
    };

    const setRank = (
        list: DatabaseSound[],
        setList: React.Dispatch<React.SetStateAction<DatabaseSound[] | null>>,
        index: number,
        rank: number | null,
    ) => {
        const sound = list[index];
        const next = [...list];
        next[index] = { ...sound, rank };
        setList(next);

        setRankedEntries(entries => {
            if (entries === null) return entries;
            const withoutThis = entries.filter(e => e.uid !== sound.uid);
            return rank === null ? withoutThis : [...withoutThis, { uid: sound.uid, rank }];
        });
    };

    const pushToTop = (
        list: DatabaseSound[],
        setList: React.Dispatch<React.SetStateAction<DatabaseSound[] | null>>,
        index: number,
    ) => {
        if (rankedEntries === null) return; // reference set still loading
        const nextRank = rankedEntries.reduce((max, e) => Math.max(max, e.rank), 0) + 1;
        setRank(list, setList, index, nextRank);
    };

    // Where a sound's current rank would land among every ranked sound
    // (across all packs), e.g. "#3 of 12". null while loading or unranked.
    const positionLabel = (sound: DatabaseSound): string | null => {
        if (sound.rank === null || rankedEntries === null) return null;
        const others = rankedEntries.filter(e => e.uid !== sound.uid);
        const higherCount = others.filter(e => e.rank > sound.rank!).length;
        return `#${higherCount + 1} of ${others.length + 1}`;
    };

    const handlePublish = async () => {
        if (!packId || !baseSounds || !demoSounds) return;
        setPublishing(true);
        setError(null);
        try {
            const combined = [...demoSounds, ...baseSounds];
            await DatabaseSoundPackSoundsModel.setPackSounds(packId, combined);
            setSavedOrder(combined.map(s => s.uid));
            setSavedRanks(Object.fromEntries(combined.map(s => [s.uid, s.rank])));
        } catch (err) {
            console.error('Failed to publish sound order:', err);
            setError('Failed to publish. Try again.');
        } finally {
            setPublishing(false);
        }
    };

    if (userLoading || loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
                    <p className="text-gray-700 mb-4">You don't have permission to access this page.</p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    const renderSection = (
        label: string,
        list: DatabaseSound[] | null,
        setList: React.Dispatch<React.SetStateAction<DatabaseSound[] | null>>,
    ) => (
        <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {label} <span className="font-normal text-gray-400">({list?.length ?? 0})</span>
            </h3>
            {!list || list.length === 0 ? (
                <p className="text-sm text-gray-400">No {label.toLowerCase()} sounds.</p>
            ) : (
                <div className="space-y-1">
                    {list.map((sound, index) => (
                        <div
                            key={sound.uid}
                            className="flex items-center gap-3 border border-gray-200 rounded-md px-3 py-2"
                        >
                            <span className="text-xs text-gray-400 w-6 text-right">{index + 1}</span>
                            <span className="flex-1 text-sm text-gray-900 truncate">{sound.title || sound.uid}</span>
                            <span className="w-10 text-xs text-gray-500 text-center">{sound.key}</span>
                            <span className="w-10 text-xs text-gray-500 text-center">{sound.bpm}</span>
                            <span className="w-12 text-xs text-gray-500 text-right">{formatDuration(sound.duration)}</span>
                            <div className="w-20 h-6 shrink-0">
                                <WaveformThumbnail data={decodeThumbnail(sound.thumbnail)} className="text-gray-400" />
                            </div>
                            <input
                                type="number"
                                value={sound.rank ?? ''}
                                onChange={e => setRank(list, setList, index, e.target.value === '' ? null : Number(e.target.value))}
                                placeholder="—"
                                aria-label="All Samples rank"
                                title="Rank in the plugin's flat All Samples list (higher = closer to the top). Blank = unranked."
                                className="w-20 text-xs text-gray-700 border border-gray-300 rounded px-1.5 py-1 text-right"
                            />
                            <span className="w-20 text-[11px] text-gray-400 shrink-0">
                                {positionLabel(sound) ?? (sound.rank !== null ? '…' : '')}
                            </span>
                            <button
                                onClick={() => pushToTop(list, setList, index)}
                                disabled={rankedEntries === null}
                                aria-label="Push to top of All Samples"
                                title="Rank above every other ranked sound"
                                className="px-2 h-7 flex items-center justify-center rounded border border-gray-300 text-xs text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Top
                            </button>
                            <button
                                onClick={() => moveSound(list, setList, index, -1)}
                                disabled={index === 0}
                                aria-label="Move up"
                                className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                ▲
                            </button>
                            <button
                                onClick={() => moveSound(list, setList, index, 1)}
                                disabled={index === list.length - 1}
                                aria-label="Move down"
                                className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                ▼
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // Distinct rank values currently in use, highest first, with how many
    // sounds share each one (ties are common — e.g. every push-to-top before
    // the next publish shares the pre-fetch max until it's bumped).
    const rankGroups = rankedEntries !== null
        ? Array.from(
            rankedEntries.reduce((counts, e) => counts.set(e.rank, (counts.get(e.rank) ?? 0) + 1), new Map<number, number>()),
        )
            .sort((a, b) => b[0] - a[0])
            .map(([value, count]) => ({ value, count }))
        : null;

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <button
                    onClick={() => navigate('/admin')}
                    className="text-sm text-blue-600 hover:text-blue-700 mb-4"
                >
                    ← Back to Edit Preview
                </button>

                <div className="flex items-start gap-6">
                    <div className="bg-white rounded-lg shadow p-6 flex-1 min-w-0">
                        <h2 className="text-xl font-semibold mb-1">Reorder Sounds</h2>
                        <p className="text-gray-600 mb-1">
                            {pack?.name || packId} — this is the default order sounds appear within this pack.
                        </p>
                        <p className="text-gray-500 text-sm mb-4">
                            Rank controls where a sound sits in the plugin's flat "All Samples" view, across every pack —
                            higher ranks first, blank stays unranked.
                        </p>

                        {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

                        {renderSection('Demo', demoSounds, setDemoSounds)}
                        {renderSection('Base', baseSounds, setBaseSounds)}

                        <button
                            onClick={handlePublish}
                            disabled={!isDirty || publishing}
                            className={`w-full px-4 py-2 rounded-md text-sm font-medium ${!isDirty || publishing
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                        >
                            {publishing ? 'Publishing...' : isDirty ? 'Publish order' : 'No changes to publish'}
                        </button>
                    </div>

                    <div className="w-40 shrink-0 sticky top-8">
                        <div className="bg-white rounded-lg shadow p-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
                            <h3 className="text-sm font-semibold text-gray-700 mb-2">Ranks</h3>
                            {rankGroups === null ? (
                                <p className="text-xs text-gray-400">Loading…</p>
                            ) : rankGroups.length === 0 ? (
                                <p className="text-xs text-gray-400">None yet.</p>
                            ) : (
                                <table className="w-full text-xs text-gray-600">
                                    <thead>
                                        <tr className="text-gray-400 text-left">
                                            <th className="font-medium pb-1">Rank</th>
                                            <th className="font-medium pb-1 text-right">Value</th>
                                            <th className="font-medium pb-1 text-right">Count</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rankGroups.map((group, index) => (
                                            <tr key={group.value}>
                                                <td className="text-gray-400">{index + 1}</td>
                                                <td className="text-right">{group.value}</td>
                                                <td className="text-right">{group.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReorderSounds;
